import { useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// ─── Data ────────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    num: "01",
    tag: "SATELLITE INTELLIGENCE",
    name: "Farm Health\nReports",
    nameSub: "Farm Health Reports",
    tagline: "Know Your Farm, Every Week",
    desc: "Personalised crop health reports generated from space — tracking how your plants are growing, how much water is in the soil, and whether your field needs attention. Plain language, no jargon, delivered weekly to your phone.",
    bgWords: ["FARM", "HEALTH"],
    accent: "#4ADE80",
    rgb: "74,222,128",
    highlights: [
      { label: "Plant Growth",    val: "Weekly Tracking"    },
      { label: "Water in Soil",   val: "Field Monitoring"   },
      { label: "Coverage",        val: "Multi-crop Support" },
      { label: "Cadence",         val: "Weekly Delivery"    },
    ],
    stats: [
      { n: "110+", l: "Acres Monitored" },
      { n: "130+", l: "Farmers Served"  },
      { n: "95%",  l: "Report Accuracy" },
    ],
    mockup: {
      type: "health",
      title: "Crop Health Overview",
      badge: "Updated 3 hrs ago",
      metrics: [
        { label: "Plant Growth",  value: 0.74, color: "#4ADE80", status: "Healthy" },
        { label: "Water in Soil", value: 0.61, color: "#38BDF8", status: "Optimal" },
        { label: "Soil Activity", value: 0.48, color: "#FACC15", status: "Monitor" },
      ],
    },
  },
  {
    num: "02",
    tag: "PRECISION FORECASTING",
    name: "Weather\nAlerts &\nForecast",
    nameSub: "Weather Alerts & Forecast",
    tagline: "Predict Before It Hits",
    desc: "Hyperlocal weather forecasts and real-time alerts tailored to your exact farm. Rainfall predictions, temperature extremes, humidity warnings — all calibrated for your crop type and delivered straight to your phone.",
    bgWords: ["WEATHER", "FORECAST"],
    accent: "#38BDF8",
    rgb: "56,189,248",
    highlights: [
      { label: "Forecast Window", val: "7-Day Rolling"      },
      { label: "Alert Types",     val: "Rain · Heat · Wind" },
      { label: "Precision",       val: "Field-Level GPS"    },
      { label: "Delivery",        val: "Push + WhatsApp"    },
    ],
    stats: [
      { n: "7",    l: "Day Forecast"   },
      { n: "< 2h", l: "Alert Response" },
      { n: "98%",  l: "Uptime"         },
    ],
    mockup: {
      type: "weather",
      title: "Live Weather Dashboard",
      days: [
        { day: "Mon", icon: "🌤", high: 34, low: 24, rain: 10 },
        { day: "Tue", icon: "🌧", high: 29, low: 22, rain: 80 },
        { day: "Wed", icon: "⛅", high: 31, low: 23, rain: 30 },
        { day: "Thu", icon: "☀️", high: 36, low: 25, rain: 5  },
        { day: "Fri", icon: "🌧", high: 28, low: 21, rain: 90 },
      ],
      alert: "Heavy rainfall expected Tuesday — defer pesticide spray",
    },
  },
  {
    num: "03",
    tag: "AI ADVISORY",
    name: "Chatbot\nAdvisory",
    nameSub: "AI Chatbot Advisory",
    tagline: "Your Personal Agri Expert",
    desc: "An always-on AI agronomist available in all regional languages. Ask about pest control, fertilizer schedules, irrigation timing, or market prices. Our AI models are regularly updated to stay current with the latest farming knowledge.",
    bgWords: ["ADVISORY", "CHATBOT"],
    accent: "#F472B6",
    rgb: "244,114,182",
    highlights: [
      { label: "Languages",        val: "All Regional Languages" },
      { label: "Advisory Modules", val: "10 Specialisations"     },
      { label: "Integration",      val: "WhatsApp Ready"         },
      { label: "AI Model Updates", val: "Regular Upgrades"       },
    ],
    stats: [
      { n: "1,560+", l: "Advisories Sent"  },
      { n: "10",     l: "Advisory Modules" },
      { n: "24/7",   l: "Always Available" },
    ],
    mockup: {
      type: "chat",
      title: "KrishiSat Advisory",
      messages: [
        { from: "farmer", text: "My paddy crop leaves are turning yellow 🌾" },
        { from: "bot",    text: "We spotted signs of nutrient stress in the north part of your field. Apply fertilizer 20kg/acre this week before rain on Tuesday." },
        { from: "farmer", text: "When should I do irrigation?" },
        { from: "bot",    text: "Skip Thursday — heavy rain forecast. Best window is Friday morning 6–8am. Your soil has enough water right now." },
      ],
    },
  },
];

const N = FEATURES.length;

// ─── Mockup sub-components ────────────────────────────────────────────────────

const HealthMockup = ({ f }) => (
  <div className="w-full space-y-3">
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs font-black tracking-widest uppercase" style={{ color: f.accent, fontFamily: "'Raleway', sans-serif", fontWeight: 900 }}>
        {f.mockup.title}
      </span>
      <span className="text-xs px-2 py-1 rounded-full" style={{ background: `rgba(${f.rgb},.15)`, color: f.accent, fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>
        {f.mockup.badge}
      </span>
    </div>
    {f.mockup.metrics.map((m, i) => (
      <div key={i} className="space-y-1.5">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-400" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600 }}>{m.label}</span>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: m.color + "20", color: m.color, fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>
            {m.status}
          </span>
        </div>
        <div className="h-1.5 rounded-full w-full" style={{ background: "rgba(255,255,255,.08)" }}>
          <div className="h-full rounded-full" style={{ width: `${m.value * 100}%`, background: m.color, boxShadow: `0 0 8px ${m.color}60` }} />
        </div>
        <div className="text-right text-xs font-black" style={{ color: m.color, fontFamily: "'Raleway', sans-serif", fontWeight: 900 }}>{m.value.toFixed(2)}</div>
      </div>
    ))}
    <div className="mt-4 rounded-xl overflow-hidden relative" style={{ height: "70px", background: "rgba(0,0,0,.3)", border: "1px solid rgba(74,222,128,.2)" }}>
      <div className="absolute inset-0 grid p-2 opacity-60" style={{ gridTemplateColumns: "repeat(8,1fr)", gap: "2px" }}>
        {Array.from({ length: 40 }).map((_, i) => (
          <div key={i} className="rounded-sm" style={{ background: `hsl(${120 + Math.sin(i * 0.8) * 30},${50 + (i * 7) % 40}%,${20 + (i * 3) % 35}%)` }} />
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold tracking-wider" style={{ color: "#4ADE80", fontFamily: "'Raleway', sans-serif", fontWeight: 700, textShadow: "0 0 10px #4ADE80" }}>
          YOUR FIELD · LIVE VIEW
        </span>
      </div>
    </div>
  </div>
);

const WeatherMockup = ({ f }) => (
  <div className="w-full space-y-3">
    <div className="flex items-center justify-between">
      <span className="text-xs font-black tracking-widest uppercase" style={{ color: f.accent, fontFamily: "'Raleway', sans-serif", fontWeight: 900 }}>{f.mockup.title}</span>
      <span className="text-xs text-gray-500" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600 }}>📍 Your Farm</span>
    </div>
    <div className="flex items-center gap-4 py-3 px-4 rounded-xl" style={{ background: `rgba(${f.rgb},.08)`, border: `1px solid rgba(${f.rgb},.2)` }}>
      <span className="text-4xl">🌤</span>
      <div>
        <div className="text-3xl font-black text-white" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 900 }}>33°C</div>
        <div className="text-xs text-gray-400" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 500 }}>Partly cloudy · Humidity 68%</div>
      </div>
      <div className="ml-auto text-right">
        <div className="text-xs font-bold" style={{ color: f.accent, fontFamily: "'Raleway', sans-serif", fontWeight: 700 }}>💧 12mm today</div>
        <div className="text-xs text-gray-500" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 500 }}>↑ 35° ↓ 23°</div>
      </div>
    </div>
    <div className="grid grid-cols-5 gap-1.5">
      {f.mockup.days.map((d, i) => (
        <div key={i} className="text-center py-2 px-1 rounded-xl"
          style={{ background: i === 1 ? `rgba(${f.rgb},.18)` : "rgba(255,255,255,.04)", border: i === 1 ? `1px solid rgba(${f.rgb},.4)` : "1px solid transparent" }}>
          <div className="text-xs text-gray-500 mb-1" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 500 }}>{d.day}</div>
          <div className="text-base">{d.icon}</div>
          <div className="text-xs font-black text-white mt-1" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 900 }}>{d.high}°</div>
          <div className="text-xs" style={{ color: f.accent, fontFamily: "'Raleway', sans-serif", fontWeight: 600 }}>{d.rain}%</div>
        </div>
      ))}
    </div>
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl" style={{ background: "rgba(251,146,60,.1)", border: "1px solid rgba(251,146,60,.3)" }}>
      <span className="text-lg mt-0.5">⚠️</span>
      <p className="text-xs text-orange-300 leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 500 }}>{f.mockup.alert}</p>
    </div>
  </div>
);

const ChatMockup = ({ f }) => {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    setVisible(0);
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setVisible(i);
      if (i >= f.mockup.messages.length) clearInterval(t);
    }, 900);
    return () => clearInterval(t);
  }, [f]);

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm"
          style={{ background: `rgba(${f.rgb},.2)`, border: `1px solid rgba(${f.rgb},.5)` }}>🌾</div>
        <div>
          <div className="text-xs font-black" style={{ color: f.accent, fontFamily: "'Raleway', sans-serif", fontWeight: 900 }}>{f.mockup.title}</div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ animation: "chatPulse 1.5s ease infinite" }} />
            <span className="text-xs text-gray-500" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 500 }}>Online · Always available</span>
          </div>
        </div>
      </div>
      <div className="space-y-2.5" style={{ minHeight: "160px" }}>
        {f.mockup.messages.map((msg, i) =>
          i < visible ? (
            <div key={i} className={`flex ${msg.from === "farmer" ? "justify-end" : "justify-start"}`}
              style={{ animation: "msgIn .4s cubic-bezier(.22,1,.36,1) both" }}>
              <div className="max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed"
                style={{
                  background: msg.from === "bot" ? `rgba(${f.rgb},.14)` : "rgba(255,255,255,.07)",
                  border: msg.from === "bot" ? `1px solid rgba(${f.rgb},.3)` : "1px solid rgba(255,255,255,.1)",
                  color: msg.from === "bot" ? "#e2e8f0" : "#cbd5e1",
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 500,
                }}>
                {msg.text}
              </div>
            </div>
          ) : null
        )}
        {visible < f.mockup.messages.length && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-2xl flex gap-1 items-center"
              style={{ background: `rgba(${f.rgb},.1)`, border: `1px solid rgba(${f.rgb},.2)` }}>
              {[0, 1, 2].map(i => (
                <div key={i} className="w-1.5 h-1.5 rounded-full"
                  style={{ background: f.accent, animation: `chatBounce .8s ease ${i * 0.15}s infinite` }} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FloatingMockup = ({ feature: f }) => (
  <div className="relative rounded-2xl p-5 w-full"
    style={{
      background: "rgba(8,12,24,0.88)",
      border: `1px solid rgba(${f.rgb},.25)`,
      backdropFilter: "blur(20px)",
      boxShadow: `0 40px 100px rgba(0,0,0,.6), 0 0 60px rgba(${f.rgb},.15), inset 0 1px 0 rgba(255,255,255,.07)`,
      width: "clamp(280px, 30vw, 400px)",
    }}>
    {/* macOS dots */}
    <div className="flex items-center gap-1.5 mb-4">
      {["#FF5F57", "#FFBD2E", "#28CA41"].map((c, i) => (
        <div key={i} className="w-2.5 h-2.5 rounded-full" style={{ background: c, opacity: 0.7 }} />
      ))}
      <div className="ml-auto flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: f.accent, animation: "chatPulse 1.5s ease infinite" }} />
        <span className="text-xs text-gray-600" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600 }}>LIVE</span>
      </div>
    </div>
    {f.mockup.type === "health"  && <HealthMockup  f={f} />}
    {f.mockup.type === "weather" && <WeatherMockup f={f} />}
    {f.mockup.type === "chat"    && <ChatMockup    f={f} />}
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Krishisat() {
  const [idx, setIdx] = useState(0);

  const wrapRef     = useRef();
  const bgTextRef   = useRef();
  const leftRef     = useRef();
  const rightRef    = useRef();
  const mockupRef   = useRef();
  const dotRefs     = useRef([]);
  const progressRef = useRef();
  const curIdx      = useRef(0);
  const busy        = useRef(false);
  const floatTL     = useRef();

  const f = FEATURES[idx];

  const GT = { background: "linear-gradient(88.52deg,#1263AF 29%,#06CCBC 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" };

  const startFloat = useCallback(() => {
    floatTL.current?.kill();
    if (!mockupRef.current) return;
    gsap.set(mockupRef.current, { y: 0, rotateZ: 0 });
    floatTL.current = gsap.timeline({ repeat: -1, yoyo: true })
      .to(mockupRef.current, { y: -14, rotateZ: 1.2, duration: 3.2, ease: "sine.inOut" })
      .to(mockupRef.current, { y: -4,  rotateZ: -0.8, duration: 2.6, ease: "sine.inOut" });
  }, []);

  const transitionTo = useCallback((next, dir = 1) => {
    if (busy.current || next === curIdx.current) return;
    busy.current = true;
    floatTL.current?.kill();

    const mockup = mockupRef.current;
    const left   = leftRef.current;
    const right  = rightRef.current;
    const bg     = bgTextRef.current;
    const D      = 0.48;

    gsap.timeline({
      onComplete: () => {
        curIdx.current = next;
        setIdx(next);

        if (progressRef.current)
          progressRef.current.style.width = `${((next + 1) / N) * 100}%`;

        dotRefs.current.forEach((d, i) => {
          if (!d) return;
          d.style.height     = i === next ? "32px" : "10px";
          d.style.background = i === next ? FEATURES[next].accent : "rgba(255,255,255,.2)";
        });

        gsap.fromTo(mockup,
          { rotateY: dir * -100, rotateX: dir * -12, scale: 0.6, y: dir * 50, opacity: 0, filter: "blur(14px)" },
          { rotateY: 0, rotateX: 0, scale: 1, y: 0, opacity: 1, filter: "blur(0px)",
            duration: D + 0.15, ease: "power3.out",
            onComplete: () => { busy.current = false; startFloat(); } }
        );
        gsap.fromTo(bg,    { opacity: 0, scale: 1.06 }, { opacity: 1, scale: 1, duration: D * 0.8, ease: "power2.out" });
        gsap.fromTo(left,  { x: dir * -80, opacity: 0 }, { x: 0, opacity: 1, duration: D, ease: "power3.out", delay: 0.06 });
        gsap.fromTo(right, { x: dir * 80,  opacity: 0 }, { x: 0, opacity: 1, duration: D, ease: "power3.out", delay: 0.06 });
      },
    })
    .to(mockup, { rotateY: dir * 100, rotateX: dir * 10, scale: 0.55, y: dir * -50, opacity: 0, filter: "blur(14px)", duration: D, ease: "power2.in" }, 0)
    .to(bg,     { opacity: 0, scale: 0.94, duration: D * 0.6, ease: "power2.in" }, 0)
    .to(left,   { x: dir * 80,  opacity: 0, duration: D * 0.7, ease: "power2.in" }, 0)
    .to(right,  { x: dir * -80, opacity: 0, duration: D * 0.7, ease: "power2.in" }, 0);
  }, [startFloat]);

  useEffect(() => {
    if (progressRef.current)
      progressRef.current.style.width = `${(1 / N) * 100}%`;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: wrapRef.current,
        start: "top top",
        end: "bottom bottom",
        onUpdate: (self) => {
          const next = Math.round(self.progress * (N - 1));
          if (next !== curIdx.current)
            transitionTo(next, next > curIdx.current ? 1 : -1);
        },
      });
    }, wrapRef);

    startFloat();
    return () => ctx.revert();
  }, [transitionTo, startFloat]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight") scrollToIdx(Math.min(N - 1, curIdx.current + 1));
      if (e.key === "ArrowUp"   || e.key === "ArrowLeft")  scrollToIdx(Math.max(0, curIdx.current - 1));
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []); // eslint-disable-line

  const scrollToIdx = (i) => {
    if (!wrapRef.current) return;
    const pct    = i / (N - 1);
    const totalH = wrapRef.current.scrollHeight - window.innerHeight;
    window.scrollTo({ top: wrapRef.current.offsetTop + pct * totalH, behavior: "smooth" });
  };

  const goTo = useCallback((i) => {
    transitionTo(i, i > curIdx.current ? 1 : -1);
    scrollToIdx(i);
  }, [transitionTo]); // eslint-disable-line

  return (
    <div style={{ fontFamily: "'Raleway', sans-serif" }} className="min-h-screen text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&display=swap');

        #ks-mockup { transform-style: preserve-3d; will-change: transform, opacity, filter; }

        #ks-bg-text {
          position: absolute; inset: 0;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          pointer-events: none; user-select: none;
        }
        .ks-bg-word {
          font-family: 'Raleway', sans-serif; font-weight: 900;
          line-height: .88; letter-spacing: -.03em; white-space: nowrap;
        }

        #ks-prog { transition: width .5s ease, background .8s ease; }
        .ks-dot  { border-radius: 2px; transition: height .4s, background .4s; cursor: pointer; display: block; }

        @keyframes mq         { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes hu         { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes msgIn      { from{opacity:0;transform:translateY(8px) scale(.95)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes chatBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        @keyframes chatPulse  { 0%,100%{opacity:.6} 50%{opacity:1} }
        @keyframes spin       { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

        .ks-mq { display: flex; width: max-content; animation: mq 30s linear infinite; }

        .hu1 { animation: hu .9s cubic-bezier(.22,1,.36,1) both  .1s; opacity: 0; }
        .hu2 { animation: hu .9s cubic-bezier(.22,1,.36,1) both  .25s; opacity: 0; }
        .hu3 { animation: hu .9s cubic-bezier(.22,1,.36,1) both  .4s; opacity: 0; }
        .hu4 { animation: hu .9s cubic-bezier(.22,1,.36,1) both  .55s; opacity: 0; }

        #ks-left, #ks-right { will-change: transform, opacity; }
      `}</style>

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative w-full flex flex-col items-center justify-center text-center px-6 pt-20 pb-10">

        {/* Orbiting satellites */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 pointer-events-none" style={{ width: "600px", height: "600px" }}>
          {/* Outer orbit */}
          <div className="absolute inset-0 rounded-full" style={{ border: "1px solid rgba(74,222,128,.12)", animation: "spin 40s linear infinite" }}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ filter: "drop-shadow(0 0 6px #4ADE80)" }}>
                <rect x="9" y="9" width="6" height="6" rx="1" fill="#4ADE80" opacity="0.9"/>
                <rect x="2" y="10.5" width="6" height="3" rx="0.5" fill="#4ADE80" opacity="0.6"/>
                <rect x="16" y="10.5" width="6" height="3" rx="0.5" fill="#4ADE80" opacity="0.6"/>
                <line x1="12" y1="9" x2="12" y2="5" stroke="#4ADE80" strokeWidth="1" opacity="0.8"/>
                <circle cx="12" cy="4" r="1" fill="#4ADE80" opacity="0.8"/>
                <circle cx="12" cy="12" r="1.5" fill="white" opacity="0.9"/>
              </svg>
            </div>
          </div>
          {/* Inner orbit */}
          <div className="absolute inset-10 rounded-full" style={{ border: "1px solid rgba(56,189,248,.09)", animation: "spin 26s linear infinite reverse" }}>
            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ filter: "drop-shadow(0 0 5px #38BDF8)" }}>
                <rect x="9" y="9" width="6" height="6" rx="1" fill="#38BDF8" opacity="0.85"/>
                <rect x="2" y="10.5" width="6" height="3" rx="0.5" fill="#38BDF8" opacity="0.55"/>
                <rect x="16" y="10.5" width="6" height="3" rx="0.5" fill="#38BDF8" opacity="0.55"/>
                <line x1="12" y1="9" x2="12" y2="5" stroke="#38BDF8" strokeWidth="1" opacity="0.7"/>
                <circle cx="12" cy="4" r="1" fill="#38BDF8" opacity="0.7"/>
                <circle cx="12" cy="12" r="1.5" fill="white" opacity="0.8"/>
              </svg>
            </div>
          </div>
        </div>

        <p className="hu1 text-xs font-bold tracking-[.35em] uppercase mb-4 relative z-10" style={{ color: "#4ADE80" }}>
          KrishiSat
        </p>
        <h1 className="hu2 font-black text-white leading-none mb-4 relative z-10"
          style={{ fontSize: "clamp(2.8rem,8vw,7.5rem)", fontFamily: "'Raleway', sans-serif", letterSpacing: "-.04em", fontWeight: 900 }}>
          Personalized<br />
          <span style={GT}>Agri Broadcast</span>
        </h1>
        <p className="hu3 text-gray-300 max-w-lg mx-auto text-lg leading-relaxed mb-8 relative z-10">
          Smart farm intelligence — delivering weekly crop health reports, weather forecasts,
          and AI advisories to every farmer, every week.
        </p>
        <div className="hu4 flex flex-wrap justify-center gap-3 mb-10 relative z-10">
          {[["110+","Acres"],["130+","Farmers"],["1,560+","Advisories"],["3","Features"]].map(([n, l], i) => (
            <div key={i} className="flex items-baseline gap-2 px-5 py-2.5 rounded-full"
              style={{ border: "1px solid rgba(255,255,255,.08)" }}>
              <span className="text-xl font-black" style={GT}>{n}</span>
              <span className="text-xs text-gray-400">{l}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 tracking-widest uppercase" style={{ animation: "chatBounce 1.5s ease infinite" }}>
          Scroll to explore features ↓
        </p>
      </section>

      {/* ── MARQUEE ──────────────────────────────────────────────── */}
      <div className="overflow-hidden border-y py-3.5 mt-4" style={{ borderColor: "rgba(255,255,255,.05)" }}>
        <div className="ks-mq">
          {[...Array(2)].map((_, o) =>
            ["Farm Health Reports","Weather Alerts","AI Chatbot Advisory","Smart Monitoring","Plant Growth","Precision Farming","WhatsApp Delivery","130+ Farmers"].map((w, i) => (
              <span key={`${o}-${i}`} className="text-xs font-bold tracking-[.3em] uppercase mx-8"
                style={{ color: i % 4 === 0 ? "#4ADE80" : "rgba(255,255,255,.07)" }}>
                {w} ·
              </span>
            ))
          )}
        </div>
      </div>

      {/* ── SCROLL WRAPPER ────────────────────────────────────────── */}
      <div ref={wrapRef} style={{ height: `${N * 100}vh`, position: "relative" }}>

        {/* STICKY PANEL */}
        <div style={{ position: "sticky", top: 0, height: "100vh", overflow: "hidden" }}>

          {/* Ambient glow */}
          <div className="absolute inset-0 pointer-events-none transition-all duration-1000"
            style={{ background: `radial-gradient(ellipse 65% 75% at 50% 50%, rgba(${f.rgb},.08) 0%, transparent 68%)` }} />

          {/* Dot grid */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ backgroundImage: `radial-gradient(circle, rgba(${f.rgb},.1) 1px, transparent 1px)`, backgroundSize: "44px 44px", opacity: .3, transition: "background-image 1s" }} />

          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 z-30" style={{ height: "2px", background: "rgba(255,255,255,.04)" }}>
            <div id="ks-prog" ref={progressRef}
              style={{ height: "100%", width: `${(1 / N) * 100}%`, background: `linear-gradient(90deg,rgba(${f.rgb},.9),rgba(${f.rgb},.3))` }} />
          </div>

          {/* Giant bg text */}
          <div id="ks-bg-text" ref={bgTextRef}>
            {f.bgWords.map((word, wi) => (
              <div key={wi} className="ks-bg-word transition-colors duration-700"
                style={{ fontSize: "clamp(4rem,13vw,12rem)", color: `rgba(${f.rgb},${wi === 0 ? .18 : .1})` }}>
                {word}
              </div>
            ))}
          </div>

          {/* ── DESKTOP layout (3 columns) ── */}
          <div className="hidden lg:flex relative z-10 h-full items-center justify-center px-12">
            <div className="w-full max-w-screen-xl grid items-center"
              style={{ gridTemplateColumns: "1fr auto 1fr", gap: "clamp(.8rem,3.5vw,4.5rem)" }}>

              {/* LEFT */}
              <div id="ks-left" ref={leftRef} className="flex flex-col space-y-5">
                <div className="flex items-end gap-3 select-none">
                  <span className="font-black leading-none transition-colors duration-700"
                    style={{ fontSize: "6rem", color: `rgba(${f.rgb},.12)`, fontFamily: "'Raleway', sans-serif", lineHeight: 1, fontWeight: 900 }}>
                    {f.num}
                  </span>
                  <div className="mb-2">
                    <span className="block text-xs font-bold tracking-[.28em] uppercase transition-colors duration-500"
                      style={{ color: f.accent }}>{f.tag}</span>
                    <div className="mt-1.5 h-0.5 w-8 rounded-full transition-all duration-500" style={{ background: f.accent }} />
                  </div>
                </div>
                <h2 className="font-black text-white leading-none"
                  style={{ fontSize: "clamp(1.8rem,2.8vw,2.8rem)", fontFamily: "'Raleway', sans-serif", letterSpacing: "-.03em", whiteSpace: "pre-line", fontWeight: 900 }}>
                  {f.name}
                </h2>
                <p className="text-xs font-bold tracking-[.22em] uppercase transition-colors duration-500" style={{ color: f.accent }}>{f.tagline}</p>
                <p className="text-gray-300 text-sm leading-relaxed" style={{ maxWidth: "300px" }}>{f.desc}</p>
                <a href="https://farmer.krishisat.in" target="_blank" rel="noopener noreferrer"
                  className="self-start px-7 py-3 rounded-full text-sm font-bold tracking-wide transition-all hover:scale-105 hover:brightness-110 inline-block"
                  style={{ background: f.accent, color: "#000", boxShadow: `0 0 28px rgba(${f.rgb},.45)`, transition: "background .5s,box-shadow .5s" }}>
                  Explore on farmer.krishisat.in →
                </a>
              </div>

              {/* CENTER */}
              <div className="flex flex-col items-center justify-center" style={{ perspective: "1000px" }}>
                <div id="ks-mockup" ref={mockupRef}>
                  <FloatingMockup feature={f} />
                </div>
                <div className="rounded-full blur-3xl -mt-4 pointer-events-none transition-all duration-700"
                  style={{ width: "60%", height: "20px", background: `rgba(${f.rgb},.2)` }} />
              </div>

              {/* RIGHT */}
              <div id="ks-right" ref={rightRef} className="flex flex-col space-y-6">
                <div>
                  <p className="text-xs font-bold tracking-[.28em] uppercase text-gray-400 mb-3">Highlights</p>
                  <div className="grid grid-cols-2 gap-2">
                    {f.highlights.map((h, i) => (
                      <div key={i} className="p-3.5 rounded-2xl transition-all duration-700"
                        style={{ border: `1px solid rgba(${f.rgb},.18)`, background: `rgba(${f.rgb},.06)` }}>
                        <div className="text-xs text-gray-400 mb-1 tracking-wide uppercase">{h.label}</div>
                        <div className="font-bold text-sm transition-colors duration-500" style={{ color: f.accent }}>{h.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold tracking-[.28em] uppercase text-gray-400 mb-3">Impact</p>
                  <div className="space-y-3">
                    {f.stats.map((s, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="font-black text-2xl min-w-[4rem]"
                          style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 900, ...GT }}>{s.n}</div>
                        <div className="text-sm text-gray-300">{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-px transition-all duration-700"
                  style={{ background: `linear-gradient(90deg,rgba(${f.rgb},.5),transparent)` }} />
                <div className="font-black text-right select-none leading-none"
                  style={{ fontSize: "4.5rem", color: `rgba(${f.rgb},.07)`, fontFamily: "'Raleway', sans-serif", fontWeight: 900, transition: "color .5s" }}>
                  {f.num}
                </div>
              </div>

            </div>
          </div>

          {/* ── MOBILE layout (vertical stack) ── */}
          <div className="lg:hidden relative z-10 h-full flex flex-col justify-between py-14 px-4">

            {/* Top: tag + name */}
            <div ref={leftRef} className="text-center space-y-1.5">
              <div className="flex items-center justify-center gap-2">
                <div className="h-px w-6" style={{ background: f.accent }} />
                <span className="text-xs font-bold tracking-[.25em] uppercase transition-colors duration-500"
                  style={{ color: f.accent }}>{f.tag}</span>
                <div className="h-px w-6" style={{ background: f.accent }} />
              </div>
              <h2 className="font-black text-white leading-none"
                style={{ fontSize: "clamp(1.5rem,7vw,2.2rem)", fontFamily: "'Raleway', sans-serif", letterSpacing: "-.03em", fontWeight: 900 }}>
                {f.nameSub}
              </h2>
              <p className="text-xs font-bold tracking-[.18em] uppercase transition-colors duration-500"
                style={{ color: f.accent, opacity: .7 }}>{f.tagline}</p>
            </div>

            {/* Center: mockup */}
            <div className="flex flex-col items-center justify-center flex-1 py-3" style={{ perspective: "900px" }}>
              <div ref={mockupRef} style={{ width: "92vw", maxWidth: "360px" }}>
                <FloatingMockup feature={f} />
              </div>
              <div className="rounded-full blur-2xl -mt-3 pointer-events-none transition-all duration-700"
                style={{ width: "50%", height: "14px", background: `rgba(${f.rgb},.2)` }} />
            </div>

            {/* Bottom: highlights + CTA */}
            <div ref={rightRef} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {f.highlights.slice(0, 4).map((h, i) => (
                  <div key={i} className="p-3 rounded-xl transition-all duration-700"
                    style={{ border: `1px solid rgba(${f.rgb},.18)`, background: `rgba(${f.rgb},.06)` }}>
                    <div className="text-gray-400 mb-0.5 tracking-wide uppercase" style={{ fontSize: "9px", fontFamily: "'Raleway', sans-serif", fontWeight: 600 }}>{h.label}</div>
                    <div className="font-bold text-xs transition-colors duration-500" style={{ color: f.accent }}>{h.val}</div>
                  </div>
                ))}
              </div>
              <a href="https://farmer.krishisat.in" target="_blank" rel="noopener noreferrer"
                className="block w-full text-center py-3 rounded-full text-sm font-bold tracking-wide transition-all"
                style={{ background: f.accent, color: "#000", boxShadow: `0 0 20px rgba(${f.rgb},.35)` }}>
                Explore on farmer.krishisat.in →
              </a>
            </div>

          </div>

          {/* Dot nav — desktop only */}
          <div className="hidden lg:flex absolute left-7 top-1/2 -translate-y-1/2 z-20 flex-col gap-2.5">
            {FEATURES.map((feat, i) => (
              <button key={i} ref={el => dotRefs.current[i] = el}
                className="ks-dot" onClick={() => goTo(i)} title={feat.nameSub}
                style={{ width: "3px", height: i === 0 ? "32px" : "10px", background: i === 0 ? FEATURES[0].accent : "rgba(255,255,255,.2)" }} />
            ))}
          </div>

          {/* Counter — desktop only */}
          <div className="hidden lg:block absolute bottom-6 right-6 z-20">
            <span className="text-sm font-black transition-colors duration-500" style={{ color: f.accent }}>{f.num}</span>
            <span className="text-sm font-bold text-gray-700"> / {String(N).padStart(2, "0")}</span>
          </div>

          {/* Arrows — always visible */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
            <button onClick={() => goTo(Math.max(0, idx - 1))} disabled={idx === 0}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
              style={{ border: `1px solid rgba(${f.rgb},${idx === 0 ? .1 : .4})`, color: idx === 0 ? "rgba(255,255,255,.15)" : f.accent }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <span className="text-xs font-black" style={{ color: f.accent }}>
              {f.num} <span className="text-gray-700 font-bold">/ {String(N).padStart(2, "0")}</span>
            </span>
            <button onClick={() => goTo(Math.min(N - 1, idx + 1))} disabled={idx === N - 1}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
              style={{ border: `1px solid rgba(${f.rgb},${idx === N - 1 ? .1 : .4})`, color: idx === N - 1 ? "rgba(255,255,255,.15)" : f.accent }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>

          {/* Tagline watermark — desktop only */}
          <div className="hidden lg:block absolute bottom-16 left-1/2 -translate-x-1/2 z-10 text-center pointer-events-none select-none">
            <p className="text-xs font-bold tracking-[.3em] uppercase transition-colors duration-500"
              style={{ color: `rgba(${f.rgb},.3)` }}>
              {f.tagline}
            </p>
          </div>

        </div>
        {/* /sticky */}

      </div>
      {/* /scroll wrapper */}

      {/* ── HOW IT WORKS ─────────────────────────────────────────── */}
      <section className="w-full max-w-6xl mx-auto px-6 py-24">
        <p className="text-xs font-bold tracking-[.3em] uppercase mb-4 text-center" style={{ color: "#4ADE80" }}>Simple Process</p>
        <h2 className="font-black text-white leading-none mb-16 text-center"
          style={{ fontSize: "clamp(2rem,5vw,4rem)", fontFamily: "'Raleway', sans-serif", letterSpacing: "-.03em", fontWeight: 900 }}>
          How <span style={GT}>It Works</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          <div className="hidden md:block absolute top-14 left-1/4 right-1/4 h-px"
            style={{ background: "linear-gradient(90deg,transparent,rgba(74,222,128,.3),transparent)" }} />
          {[
            { num: "01", icon: "📝", title: "Register Your Farm",      desc: "Sign up at farmer.krishisat.in and drop a pin on your farm boundary. Takes under 2 minutes." },
            { num: "02", icon: "🛰️", title: "We Watch From Space",     desc: "We check on your farm every week from above — plant health, water levels, and soil condition, all analysed for you." },
            { num: "03", icon: "📲", title: "Insights to Your Phone",  desc: "Personalised advisory reports land on your phone weekly — in your language, with actionable guidance." },
          ].map((step, i) => (
            <div key={i} className="relative p-8 rounded-2xl text-center transition-all duration-300 hover:-translate-y-1"
              style={{ border: "1px solid rgba(74,222,128,.12)", background: "rgba(74,222,128,.03)" }}>
              <div className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center text-2xl"
                style={{ background: "rgba(74,222,128,.1)", border: "1px solid rgba(74,222,128,.25)" }}>
                {step.icon}
              </div>
              <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#4ADE80" }}>{step.num}</div>
              <h3 className="font-black text-white mb-3 text-lg" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 900 }}>{step.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── IMPACT STATS ─────────────────────────────────────────── */}
      <section className="w-full max-w-6xl mx-auto px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl p-10 md:p-14"
          style={{ border: "1px solid rgba(74,222,128,.12)", background: "linear-gradient(135deg,rgba(74,222,128,.05) 0%,rgba(56,189,248,.05) 100%)" }}>
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full pointer-events-none"
            style={{ border: "1px solid rgba(74,222,128,.06)", animation: "spin 30s linear infinite" }} />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full pointer-events-none"
            style={{ border: "1px solid rgba(56,189,248,.06)", animation: "spin 22s linear infinite reverse" }} />
          <p className="text-xs font-bold tracking-[.3em] uppercase mb-3 text-center" style={{ color: "#4ADE80" }}>Our Impact</p>
          <h2 className="font-black text-white text-center mb-12"
            style={{ fontSize: "clamp(2rem,4vw,3.5rem)", fontFamily: "'Raleway', sans-serif", letterSpacing: "-.03em", fontWeight: 900 }}>
            Real Farms. <span style={GT}>Real Results.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "110+",   l: "Acres Monitored",   sub: "and growing every season" },
              { n: "130+",   l: "Farmers Served",    sub: "across multiple states"   },
              { n: "1,560+", l: "Advisory Messages", sub: "delivered via WhatsApp"   },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-black mb-2" style={{ fontSize: "clamp(2.5rem,5vw,4rem)", fontFamily: "'Raleway', sans-serif", fontWeight: 900, ...GT }}>
                  {s.n}
                </div>
                <div className="text-white font-bold mb-1">{s.l}</div>
                <div className="text-gray-400 text-xs">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="w-full max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl p-12 md:p-16 text-center"
          style={{ border: "1px solid rgba(255,255,255,.06)" }}>
          <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full pointer-events-none"
            style={{ border: "1px solid rgba(74,222,128,.07)", animation: "spin 25s linear infinite" }} />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full pointer-events-none"
            style={{ border: "1px solid rgba(56,189,248,.07)", animation: "spin 18s linear infinite reverse" }} />
          <div className="relative z-10">
            <p className="text-xs font-bold tracking-[.3em] uppercase mb-4" style={{ color: "#4ADE80" }}>Ready to Start?</p>
            <h2 className="font-black text-white leading-none mb-6"
              style={{ fontSize: "clamp(2rem,5vw,4rem)", fontFamily: "'Raleway', sans-serif", letterSpacing: "-.03em", fontWeight: 900 }}>
              Your farm deserves<br /><span style={GT}>smart intelligence.</span>
            </h2>
            <p className="text-gray-400 max-w-lg mx-auto mb-8 leading-relaxed text-sm">
              Join 130+ farmers already growing smarter with KrishiSat.
              Sign up free at farmer.krishisat.in and get your first report this week.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="https://farmer.krishisat.in" target="_blank" rel="noopener noreferrer"
                className="px-10 py-4 rounded-full text-black font-black text-sm tracking-wide transition-all hover:scale-105 inline-block"
                style={{ background: "#4ADE80", boxShadow: "0 8px 40px rgba(74,222,128,.35)" }}>
                Visit farmer.krishisat.in →
              </a>
            </div>
            <p className="mt-6 text-xs text-gray-600">🌱 Grow smarter, harvest better · Powered by KrishiSat · X Boson AI</p>
          </div>
        </div>
      </section>

    </div>
  );
}